import type { Theme } from '@/contexts/theme.context'
import { BadgeAppearance } from '@/enum/global.enum'
import type { CSSProperties } from 'react'

interface Rgb {
  r: number
  g: number
  b: number
}

const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const WHITE: Rgb = { r: 255, g: 255, b: 255 }

/**
 * Surface a colour-coded badge is measured against, per theme.
 *
 * Badges sit on cards far more often than on the page itself, so these mirror the
 * `--card` token from `index.css`. The dark card is the lightest dark surface, which
 * makes it the worst case for contrast - anything readable here is readable elsewhere.
 */
const BADGE_SURFACE: Record<Theme, Rgb> = {
  light: { r: 255, g: 255, b: 255 }, // oklch(1 0 0)
  dark: { r: 23, g: 23, b: 23 }, // oklch(0.205 0 0)
}

/** WCAG AA for small text. Used for outline badge text and borders. */
const TEXT_CONTRAST = 4.5
/** WCAG AA for non-text UI boundaries. Used for the edge of a filled badge. */
const EDGE_CONTRAST = 3

/** How far each step nudges the colour toward black/white while hunting for contrast. */
const ADJUST_STEP = 0.04

/**
 * Calculates the relative luminance of a color using WCAG standards
 * Returns a value between 0 (darkest) and 1 (lightest)
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    val = val / 255
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Parses a hex color string and returns RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  hex = hex.replace('#', '')

  // Handle 3-digit hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  // Handle 6-digit hex
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    return { r, g, b }
  }

  return null
}

/**
 * Parses an RGB/RGBA color string and returns RGB values
 */
function rgbToRgb(rgb: string): { r: number; g: number; b: number } | null {
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
    }
  }
  return null
}

/**
 * Parses a hex, rgb() or rgba() colour string into RGB channels.
 * Returns null for anything it cannot read, so callers can fall back safely.
 */
function parseColour(colour: string | undefined | null): Rgb | null {
  if (!colour) return null

  const value = colour.trim()

  if (value.startsWith('#')) return hexToRgb(value)
  if (value.toLowerCase().startsWith('rgb')) return rgbToRgb(value)

  return null
}

function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0')

  return `#${channel(r)}${channel(g)}${channel(b)}`
}

/** Blends `from` toward `to`, where `amount` is 0 (unchanged) to 1 (fully `to`). */
function mixColour(from: Rgb, to: Rgb, amount: number): Rgb {
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  }
}

/** WCAG contrast ratio between two colours, from 1 (identical) to 21 (black on white). */
function getContrastRatio(a: Rgb, b: Rgb): number {
  const luminanceA = getLuminance(a.r, a.g, a.b)
  const luminanceB = getLuminance(b.r, b.g, b.b)
  const [lighter, darker] = luminanceA > luminanceB ? [luminanceA, luminanceB] : [luminanceB, luminanceA]

  return (lighter + 0.05) / (darker + 0.05)
}

const readableColourCache = new Map<string, string>()

/**
 * Returns a theme-safe version of a stored colour: the colour itself when it already
 * stands out against the theme's surface, otherwise the same hue darkened (light theme)
 * or lightened (dark theme) just far enough to clear `targetRatio`.
 *
 * This is what stops an admin-picked pale yellow from vanishing on the light theme and a
 * near-black from vanishing on the dark theme, without repainting colours that are fine.
 * Blending toward black keeps the hue exactly; blending toward white keeps the hue and
 * softens the saturation, which is how pastels read well on dark backgrounds.
 *
 * @example
 * getReadableColour('#FFF3B0', 'light') // '#8a7c15' - pale yellow, now legible on white
 * getReadableColour('#FFF3B0', 'dark')  // '#FFF3B0' - already legible, left alone
 */
export function getReadableColour(
  colour: string | undefined | null,
  theme: Theme,
  targetRatio: number = TEXT_CONTRAST
): string | undefined {
  if (!colour) return undefined

  const cacheKey = `${colour}|${theme}|${targetRatio}`
  const cached = readableColourCache.get(cacheKey)
  if (cached) return cached

  const rgb = parseColour(colour)
  // Unreadable format (named colours, oklch, ...) - hand it back untouched
  if (!rgb) return colour

  const surface = BADGE_SURFACE[theme]
  const towards = theme === 'light' ? BLACK : WHITE

  let result = rgbToHex(towards)

  for (let amount = 0; amount <= 1; amount += ADJUST_STEP) {
    const candidate = mixColour(rgb, towards, amount)

    if (getContrastRatio(candidate, surface) >= targetRatio) {
      // amount === 0 means the original colour already passed, so it is returned as-is
      result = amount === 0 ? colour : rgbToHex(candidate)
      break
    }
  }

  readableColourCache.set(cacheKey, result)

  return result
}

/**
 * Builds the inline style for a colour-coded badge in the given appearance and theme.
 *
 * Filled badges keep the admin's colour as the fill - it is their choice and the label
 * flips to black or white to stay readable on it - but take a contrast-corrected border
 * so a near-white or near-black badge still has a visible edge against the page.
 * Outline badges carry the colour as text and border, so the colour itself is corrected
 * to stay legible against the theme's surface.
 *
 * Lives outside React so the PDF export can render chips that match the screen.
 */
export function getBadgeColourStyle(
  colour: string | undefined | null,
  appearance: BadgeAppearance,
  theme: Theme
): CSSProperties | undefined {
  if (!colour) return undefined

  if (appearance === BadgeAppearance.FILLED)
    return {
      backgroundColor: colour,
      borderColor: getReadableColour(colour, theme, EDGE_CONTRAST),
      color: getContrastTextColor(colour),
    }

  const readable = getReadableColour(colour, theme, TEXT_CONTRAST)

  return { backgroundColor: 'transparent', borderColor: readable, color: readable }
}

/**
 * Determines the appropriate text color (black or white) based on background color
 * Uses WCAG relative luminance calculation for accurate contrast
 *
 * @param backgroundColor - Color in hex (#RRGGBB, #RGB), rgb(), or rgba() format
 * @returns 'black' for light backgrounds, 'white' for dark backgrounds, or 'black' as fallback
 */
export function getContrastTextColor(backgroundColor: string | undefined | null): string {
  const rgb = parseColour(backgroundColor)

  if (!rgb) {
    // Fallback to black if color format is not recognized
    return 'black'
  }

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b)
  // Use threshold of 0.5 (WCAG standard suggests 0.5 for contrast ratio)
  // Luminance > 0.5 means light background, use black text
  // Luminance <= 0.5 means dark background, use white text
  return luminance > 0.5 ? 'black' : 'white'
}
